import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/lib/seo'

const EFFECTIVE_DATE = 'May 27, 2026'
const CONTACT_EMAIL = 'info@cloudnineai.com'
const GOVERNING_LAW = '[TODO: jurisdiction, e.g., Delaware, USA]'

export default function Terms() {
  const { t } = useTranslation()
  useDocumentTitle(`${t('footer.terms')} — ${t('site.name')}`)

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-8 py-24 prose prose-invert">
      <h1>{t('footer.terms')}</h1>
      <p className="opacity-70">
        <strong>Effective date:</strong> {EFFECTIVE_DATE}
      </p>

      <p>
        These Terms of Use (&quot;<strong>Terms</strong>&quot;) govern your access to and
        use of this website (the &quot;<strong>Site</strong>&quot;) operated by CloudnineAI
        (a Relavance Group company, &quot;<strong>we</strong>&quot;,
        &quot;<strong>us</strong>&quot;). By using the Site you agree to these Terms. If
        you don&apos;t agree, please don&apos;t use the Site.
      </p>
      <p>
        These Terms cover this marketing site only. Use of the ODE product, APIs, MCP
        servers, or SDK is governed by a separate agreement we provide when you receive
        access.
      </p>

      <h2>1. What this Site is</h2>
      <p>
        The Site provides information about ODE and lets you request early access. ODE is
        in research preview; everything described here is informational and may change
        without notice. Nothing on the Site is a commitment to deliver a specific feature
        on a specific timeline.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 16 years old to use this Site. If you&apos;re using the Site
        on behalf of a company, you represent that you have authority to bind that company
        to these Terms.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          access or use the Site in any way that violates applicable law or infringes
          someone else&apos;s rights;
        </li>
        <li>
          attempt to interfere with, overload, or disrupt the Site or its underlying
          infrastructure, including by scraping at scale, bypassing rate limits or
          Turnstile, or probing for vulnerabilities without authorisation;
        </li>
        <li>
          reverse engineer, decompile, or otherwise try to extract source code from the
          Site, except to the extent that law expressly permits;
        </li>
        <li>
          submit information that&apos;s false, misleading, or impersonates another person;
          or
        </li>
        <li>
          use the Site to develop a competing product by copying its design, copy, or
          structure.
        </li>
      </ul>
      <p>
        Authorised security research is welcome. Please email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> before testing.
      </p>

      <h2>4. Intellectual property</h2>
      <p>
        The Site, including its text, graphics, logos, and code, is owned by CloudnineAI or
        its licensors and is protected by intellectual-property laws. &quot;ODE,&quot;
        &quot;CloudnineAI,&quot; and the Relavance marks are trademarks of CloudnineAI /
        Relavance Group. We grant you a limited, non-exclusive, non-transferable,
        revocable licence to view the Site for your personal or internal business use. No
        other rights are granted.
      </p>

      <h2>5. Your submissions</h2>
      <p>
        When you submit information through the Site (e.g., the early-access form,
        newsletter, or feedback), you grant us a non-exclusive, worldwide, royalty-free
        licence to use, store, and process that information to respond to you, operate the
        Site, and improve our products. Don&apos;t send confidential information you
        don&apos;t want us to receive. How we handle personal data is described in our
        Privacy Policy.
      </p>

      <h2>6. Third-party links and services</h2>
      <p>
        The Site may link to third-party sites or rely on third-party services (for
        example, Cloudflare and PostHog). We don&apos;t control those services and
        aren&apos;t responsible for their content, terms, or privacy practices.
      </p>

      <h2>7. Beta and forward-looking statements</h2>
      <p>
        Anything on the Site describing future product capabilities, roadmaps, or
        performance is forward-looking and reflects our current expectations. Actual
        results may differ. Beta features are provided &quot;as is&quot; without service
        commitments.
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        THE SITE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE
        MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
        INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM COURSE OF DEALING OR
        USAGE OF TRADE. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, SECURE, OR
        ERROR-FREE.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL WE OR OUR AFFILIATES BE
        LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
        OR FOR LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO
        YOUR USE OF THE SITE. OUR TOTAL LIABILITY ARISING FROM OR RELATING TO THESE TERMS
        OR THE SITE WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS (USD 100).
      </p>
      <p>
        Some jurisdictions don&apos;t allow the exclusion of certain warranties or the
        limitation of liability for certain damages, so some of the above may not apply to
        you.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless CloudnineAI, Relavance Group,
        and our officers, employees, and agents from any claim or demand, including
        reasonable attorneys&apos; fees, arising out of your misuse of the Site or your
        breach of these Terms.
      </p>

      <h2>11. Changes to the Site or these Terms</h2>
      <p>
        We may modify the Site or these Terms at any time. If we make material changes
        to these Terms, we&apos;ll update the effective date above and, where appropriate,
        provide additional notice. Your continued use of the Site after changes take
        effect constitutes acceptance.
      </p>

      <h2>12. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of {GOVERNING_LAW}, without regard to its
        conflict-of-laws rules. Disputes will be resolved exclusively in the courts located
        in {GOVERNING_LAW}, except where mandatory consumer-protection law in your country
        provides otherwise. Nothing in these Terms limits any non-waivable rights that
        apply to you as a consumer.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </section>
  )
}
